import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/common/ui/button'
import React from 'react'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Card } from '@/common/ui/card'
import { useNavigate } from 'react-router-dom'
import UploadImage from '@/common/components/UploadImage'
import { getEventBannerUploadFileUrl } from '../../api/services'
import { PERIOD_TYPE_OPTIONS } from '../../constants'
import type { PeriodType, TurnoverEventData } from '../../type'
import { format } from 'date-fns'
import {
	WalletConditionSection,
	prepareWalletConditionForSubmit,
	type WalletConditionFormValues,
} from '@/common/components/WalletConditionSection'
import { Checkbox } from '@/common/ui/checkbox'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import { GameProviderSelector } from '@/modules/promotion/components/GameProviderSelector'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { GameType } from '@/modules/promotion/type'
import { toast } from 'sonner'
import { RtpConfirmDialog } from '@/modules/promotion/components/RtpConfirmDialog'

const formSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกชื่อกิจกรรม'),
	description: z.string().optional().nullable(),
	bannerUrl: z.string().optional().nullable(),
	periodType: z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM']),
	requiredTurnover: z.number().min(0, 'ยอดเทิร์นโอเวอร์ต้องมากกว่า 0'),
	rewardAmount: z.number().min(0, 'รางวัลต้องมากกว่า 0'),
	gameTypes: z.array(z.string()).optional(),
	// allowedGameIds: z.string().optional(), // TODO: Not yet implemented
	allowedProviderIds: z.array(z.string()).optional(),
	maxClaimsPerPlayer: z.number().optional().nullable(),
	walletCondition: z.any().optional(),
	useWalletCondition: z.boolean().default(false),
})

export type FormValues = z.infer<typeof formSchema>

interface Props {
	defaultValues?: Partial<TurnoverEventData>
	onSubmit: (values: any) => void
	mode: 'create' | 'edit'
}

export function TurnoverEventForm({ defaultValues, onSubmit, mode }: Props) {
	const navigate = useNavigate()
	const { dateFilter, setDateFilter } = useDateFilter()
	const [showRtpConfirm, setShowRtpConfirm] = React.useState(false)
	const [pendingSubmitData, setPendingSubmitData] =
		React.useState<unknown>(null)

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			title: defaultValues?.title || '',
			description: defaultValues?.description || null,
			bannerUrl: defaultValues?.bannerUrl || null,
			periodType: (defaultValues?.periodType as PeriodType) || 'DAILY',
			requiredTurnover: defaultValues?.requiredTurnover
				? Number(defaultValues.requiredTurnover)
				: 0,
			rewardAmount: defaultValues?.rewardAmount
				? Number(defaultValues.rewardAmount)
				: 0,
			gameTypes: defaultValues?.allowedGameTypes
				? typeof defaultValues.allowedGameTypes === 'string'
					? defaultValues.allowedGameTypes.split(',').filter(Boolean)
					: defaultValues.allowedGameTypes
				: [],
			// allowedGameIds: defaultValues?.allowedGameIds || '', // TODO: Not yet implemented
			allowedProviderIds: defaultValues?.allowedProviderIds
				? typeof defaultValues.allowedProviderIds === 'string'
					? defaultValues.allowedProviderIds.split(',').filter(Boolean)
					: defaultValues.allowedProviderIds
				: [],
			maxClaimsPerPlayer: defaultValues?.maxRewardsPerPlayer ?? null,
			walletCondition: defaultValues?.walletCondition || undefined,
			useWalletCondition: !!defaultValues?.walletCondition,
		},
	})

	const useWalletCondition = form.watch('useWalletCondition')
	const turnoverRound = form.watch('walletCondition.turnoverRound')

	const { data: providersData } = useQueryGetGameProviderList({
		skip: 0,
		take: 1000,
	})

	// Initialize date filter with existing dates when editing
	React.useEffect(() => {
		if (mode === 'edit' && defaultValues?.startDate && defaultValues?.endDate) {
			setDateFilter({
				startDate: format(new Date(defaultValues.startDate), 'yyyy-MM-dd'),
				endDate: format(new Date(defaultValues.endDate), 'yyyy-MM-dd'),
			})
		}
	}, [mode, defaultValues?.startDate, defaultValues?.endDate, setDateFilter])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		// Validate date filter
		if (!dateFilter.startDate || !dateFilter.endDate) {
			toast.error('กรุณาเลือกช่วงวันที่กิจกรรม')
			return
		}

		const isValid = await form.trigger()
		if (!isValid) return

		const values = form.getValues()

		const submitData: any = {
			title: values.title,
			description: values.description || undefined,
			bannerUrl: values.bannerUrl || undefined,
			startDate: new Date(`${dateFilter.startDate}T00:00:00`).toISOString(),
			endDate: new Date(`${dateFilter.endDate}T23:59:59`).toISOString(),
			periodType: values.periodType,
			requiredTurnover: values.requiredTurnover,
			minBetAmount: 0,
			rewardAmount: values.rewardAmount,
			allowedGameTypes: values.gameTypes?.join(',') || undefined,
			// allowedGameIds: values.allowedGameIds || undefined, // TODO: Not yet implemented
			allowedProviderIds: values.allowedProviderIds?.join(',') || undefined,
			maxClaimsPerPlayer: values.maxClaimsPerPlayer || undefined,
		}

		if (useWalletCondition && values.walletCondition) {
			submitData.walletCondition = prepareWalletConditionForSubmit(
				values.walletCondition as WalletConditionFormValues,
			)
		}

		setPendingSubmitData(submitData)
		setShowRtpConfirm(true)
	}

	const preventMinusInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === '-' || e.key === 'e') {
			e.preventDefault()
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={handleSubmit} className="space-y-8">
				<div className="space-y-8">
					{/* Basic Information */}
					<Card className="p-6">
						<h2 className="mb-6 text-xl font-semibold">
							ข้อมูลกิจกรรมเทิร์นโอเวอร์
						</h2>
						<div className="space-y-6">
							<FormField
								control={form.control}
								name="title"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>ชื่อกิจกรรม</FormLabel>
										<FormControl>
											<Input placeholder="กรอกชื่อกิจกรรม" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>คำอธิบาย</FormLabel>
										<FormControl>
											<Textarea
												placeholder="กรอกคำอธิบายกิจกรรม"
												className="min-h-[100px]"
												{...field}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="bannerUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Banner กิจกรรม (สัดส่วน 16:9)</FormLabel>
										<FormControl>
											<UploadImage
												onImageUploaded={(fileUrl) => field.onChange(fileUrl)}
												defaultValue={field.value ?? undefined}
												getFileUrl={({ payload }) =>
													getEventBannerUploadFileUrl({
														payload: { fileExtension: payload.fileExtension },
													})
												}
												aspectRatio="16/9"
												previewHeight={200}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</Card>

					{/* Event Period */}
					<Card className="p-6">
						<h2 className="mb-6 text-xl font-semibold">ช่วงเวลากิจกรรม</h2>

						<div className="mb-6">
							<FormLabel required>เลือกช่วงวันที่กิจกรรม</FormLabel>
							<div className="mt-2">
								<DateRangePicker />
							</div>
						</div>

						<FormField
							control={form.control}
							name="periodType"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>รอบการจ่ายรางวัล</FormLabel>
									<Select onValueChange={field.onChange} value={field.value}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกรอบการจ่าย" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{PERIOD_TYPE_OPTIONS.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormDescription>
										รอบการรีเซ็ตเทิร์นโอเวอร์สำหรับการรับรางวัลซ้ำ
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					</Card>

					{/* Turnover & Reward Configuration */}
					<Card className="p-6">
						<h2 className="mb-6 text-xl font-semibold">
							การตั้งค่าเทิร์นโอเวอร์และรางวัล
						</h2>
						<div className="space-y-6">
							<FormField
								control={form.control}
								name="requiredTurnover"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											เทิร์นโอเวอร์ที่ต้องการ (บาท)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												placeholder="0.00"
												onKeyDown={preventMinusInput}
												value={field.value ?? ''}
												onNumberChange={(value) => field.onChange(value ?? 0)}
											/>
										</FormControl>
										<FormDescription>
											ยอดเทิร์นโอเวอร์ที่ผู้เล่นต้องทำเพื่อรับรางวัล
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="rewardAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>รางวัล (บาท)</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												placeholder="0.00"
												onKeyDown={preventMinusInput}
												value={field.value ?? ''}
												onNumberChange={(value) => field.onChange(value ?? 0)}
											/>
										</FormControl>
										<FormDescription>
											จำนวนเงินที่จะจ่ายเมื่อผู้เล่นทำเทิร์นโอเวอร์ครบ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="maxClaimsPerPlayer"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											จำนวนครั้งสูงสุดต่อคน (ไม่จำกัด = เว้นว่าง)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="ไม่จำกัด"
												onKeyDown={preventMinusInput}
												value={field.value ?? ''}
												onNumberChange={(value) => field.onChange(value)}
											/>
										</FormControl>
										<FormDescription>
											จำนวนครั้งสูงสุดที่ผู้เล่นจะได้รับรางวัลต่อรอบ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</Card>

					{/* Game Filters */}
					<Card className="p-6">
						<h2 className="mb-6 text-xl font-semibold">
							กรองเกมที่นับเทิร์นโอเวอร์
						</h2>
						<div className="space-y-6">
							<FormField
								control={form.control}
								name="gameTypes"
								render={() => (
									<FormItem>
										<FormLabel>
											ประเภทเกมที่เข้าร่วมกิจกรรม (ไม่เลือกเท่ากับอนุญาตทั้งหมด)
										</FormLabel>
										<div className="flex flex-wrap gap-2 sm:gap-3">
											{[
												GameType.SLOT,
												GameType.FISHING,
												GameType.CASINO,
												GameType.LOTTO,
												GameType.SPORT,
											].map((type) => (
												<FormField
													key={type}
													control={form.control}
													name="gameTypes"
													render={({ field }) => (
														<FormItem className="flex items-center gap-2">
															<FormControl>
																<Checkbox
																	checked={field.value?.includes(type as any)}
																	onCheckedChange={(checked) => {
																		const updatedValue = checked
																			? [...(field?.value || []), type]
																			: field.value?.filter(
																					(value: string) => value !== type,
																				)
																		field.onChange(updatedValue)
																	}}
																/>
															</FormControl>
															<FormLabel>{type}</FormLabel>
														</FormItem>
													)}
												/>
											))}
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="allowedProviderIds"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											ค่ายเกมที่เข้าร่วมกิจกรรม (ไม่เลือกเท่ากับอนุญาตทั้งหมด)
										</FormLabel>
										<FormControl>
											<GameProviderSelector
												field={{
													value: field.value || [],
													onChange: field.onChange,
												}}
												gameProviders={providersData?.data}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* TODO: allowedGameIds - Not yet implemented
							<FormField
								control={form.control}
								name="allowedGameIds"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ID เกมที่กำหนด</FormLabel>
										<FormControl>
											<Input
												placeholder="UUID1,UUID2,UUID3 (เว้นว่าง = ทุกเกม)"
												{...field}
												value={field.value || ''}
											/>
										</FormControl>
										<FormDescription>
											ID เกมเฉพาะที่ต้องการนับเทิร์นโอเวอร์ คั่นด้วยจุลภาค (เว้นว่าง = ทุกเกม)
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							*/}
						</div>
					</Card>

					{/* Wallet Condition */}
					<Card className="p-6">
						<div className="mb-6 space-y-4">
							<h2 className="text-xl font-semibold">เงื่อนไขกระเป๋าเงิน</h2>
							<FormField
								control={form.control}
								name="useWalletCondition"
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel>ใช้เงื่อนไขกระเป๋าเงิน</FormLabel>
											<FormDescription>
												เปิดใช้งานเพื่อกำหนดเงื่อนไขเพิ่มเติมสำหรับการเข้าร่วมกิจกรรม
											</FormDescription>
										</div>
									</FormItem>
								)}
							/>
						</div>

						{useWalletCondition && (
							<WalletConditionSection
								control={form.control}
								fieldPrefix="walletCondition"
								showGameSettings={false}
								showPoolPreset={true}
								hideMaximumWithdraw={false}
								hideProviderVersionSettings={false}
								turnoverRound={turnoverRound}
							/>
						)}
					</Card>

					{/* Action Buttons */}
					<div className="flex items-center gap-4 pb-8">
						<Button type="submit" variant="default" className="w-full">
							{mode === 'create' ? 'สร้างกิจกรรม' : 'บันทึกการแก้ไข'}
						</Button>
						<Button
							type="button"
							variant="outline"
							className="w-full"
							onClick={() => navigate(-1)}
						>
							ยกเลิก
						</Button>
					</div>
				</div>
			</form>
			<RtpConfirmDialog
				open={showRtpConfirm}
				onOpenChange={setShowRtpConfirm}
				onConfirm={() => {
					setShowRtpConfirm(false)
					if (pendingSubmitData) {
						onSubmit(pendingSubmitData)
						setPendingSubmitData(null)
					}
				}}
				confirmLabel={mode === 'create' ? 'สร้างกิจกรรม' : 'บันทึกการแก้ไข'}
			/>
		</Form>
	)
}
